import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import MicRoundedIcon from "@mui/icons-material/MicRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import KeyboardRoundedIcon from "@mui/icons-material/KeyboardRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import PowerSettingsNewRoundedIcon from "@mui/icons-material/PowerSettingsNewRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { ownerApi } from "../../../../api/owner";
import { formatApiError } from "../../../../api/client";
import { useOwnerVoiceOperator } from "../voice/OwnerVoiceOperatorContext";
import { surfaceSx } from "./ownerCenterUi";
import { explainOwnerJarvisReasons } from "../jarvis/ownerJarvisReasonCodes";
import { persistOwnerJarvisTurn, readOwnerJarvisStoredTurn } from "../jarvis/ownerJarvisStorage";
import { appendOwnerJarvisConversationMessage, ownerJarvisConversationContext } from "../jarvis/ownerJarvisConversationJournal";
import { executeOwnerJarvisActionPlan, ownerJarvisActionExecutionFailureReport, ownerJarvisActionExecutionNotice, ownerJarvisActionExecutionSuccessReport } from "../jarvis/ownerJarvisUiExecutor";


const JARVIS_MUTATION_SCOPES = [
  "today",
  "commitments",
  "projects",
  "people",
  "waiting",
  "calendar",
];

const JARVIS_COLLAPSED_STORAGE_KEY = "wp-owner-jarvis-collapsed-v1";

function readCollapsedPreference() {
  if (typeof window === "undefined") return null;
  try {
    const value = window.localStorage.getItem(JARVIS_COLLAPSED_STORAGE_KEY);
    if (value == null) return null;
    return value === "1";
  } catch { return null; }
}

function persistCollapsedPreference(value) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(JARVIS_COLLAPSED_STORAGE_KEY, value ? "1" : "0"); }
  catch { /* localStorage opcional */ }
}

function isRootNavigationTurn(turn) {
  return String(turn?.session?.kind || "") === "navigation"
    && String(turn?.session?.branch || "root") === "root";
}

function spokenTurn(turn) {
  const why = explainOwnerJarvisReasons(turn?.reasons);
  return [turn?.reply, why, turn?.question].filter(Boolean).join(" ").trim();
}

/**
 * JARVIS_PATCH_NOTE [UX-CONTINUITY-002]
 * INVARIANTE: una interacción larga debe mostrar qué estado conserva Jarvis
 * (especialmente borradores no enviados) sin obligar al Owner a recordar el hilo.
 * PRÓXIMO PATCH: reutilizar turn.uiState para nuevos estados operativos; no inferir
 * estados críticos desde frases del reply ni sumar popups/confirmaciones paralelas.
 */
function OwnerJarvisUiState({ turn, compact = false }) {
  const state = turn?.uiState;
  if (!state) return null;

  if (state.mode === "operational_draft") {
    const items = Array.isArray(state.items) ? state.items : [];
    const warnings = Array.isArray(state.warnings) ? state.warnings : [];
    const movementProgress = state.movementProgress || null;
    const counts = state.counts || {};
    const countLabel = [
      counts.clients ? `${counts.clients} persona${counts.clients === 1 ? "" : "s"}` : "",
      counts.projects ? `${counts.projects} proyecto${counts.projects === 1 ? "" : "s"}` : "",
      counts.pendings ? `${counts.pendings} pendiente${counts.pendings === 1 ? "" : "s"}` : "",
      counts.movements ? `${counts.movements} movimiento${counts.movements === 1 ? "" : "s"}` : "",
      counts.meals ? `${counts.meals} comida${counts.meals === 1 ? "" : "s"}` : "",
    ].filter(Boolean).join(" · ");
    return (
      <Box sx={{ mt: compact ? .55 : .8, px: compact ? .75 : 1, py: compact ? .65 : .9, border: "1px solid", borderColor: "divider", borderRadius: 2.2, bgcolor: "action.hover" }}>
        <Stack direction="row" spacing={.55} alignItems="center" useFlexGap flexWrap="wrap">
          <Typography sx={{ fontWeight: 1000, fontSize: compact ? 10.8 : 11.5 }}>{state.title || "Carga operativa"}</Typography>
          <Chip size="small" label="Borrador · sin guardar" variant="outlined" sx={{ height: 22, fontWeight: 900, fontSize: 10 }} />
          {movementProgress ? <Chip size="small" label={`Movimiento ${movementProgress.current}/${movementProgress.total}`} sx={{ height: 22, fontWeight: 900, fontSize: 10 }} /> : null}
          {countLabel ? <Typography sx={{ color: "text.secondary", fontWeight: 850, fontSize: compact ? 10.2 : 10.8 }}>{countLabel}</Typography> : null}
        </Stack>
        {state.subtitle ? <Typography sx={{ mt: .35, color: "text.secondary", fontWeight: 750, fontSize: compact ? 10.4 : 11, lineHeight: 1.35 }}>{state.subtitle}</Typography> : null}
        {warnings.length ? (
          <Stack spacing={.25} sx={{ mt: .45 }}>
            {warnings.slice(0, 3).map((warning, index) => (
              <Typography key={`${warning}-${index}`} sx={{ color: "warning.main", fontWeight: 900, fontSize: compact ? 10.2 : 10.8, lineHeight: 1.3 }}>• {warning}</Typography>
            ))}
          </Stack>
        ) : null}
        {items.length ? (
          <Stack spacing={.45} sx={{ mt: .6, maxHeight: compact ? 120 : 190, overflowY: "auto" }}>
            {items.slice(0, compact ? 6 : 12).map((item, index) => (
              <Box key={`${item.kind || "item"}-${item.label || index}-${index}`} sx={{ display: "flex", gap: .65, alignItems: "baseline", minWidth: 0 }}>
                <Chip size="small" label={{ client: "CRM", project: "Proyecto", pending: "Pendiente", idea: "Idea", movement: "Movimiento", meal: "Comida" }[item.kind] || "Dato"} sx={{ height: 20, fontWeight: 900, fontSize: 9.5, flex: "0 0 auto" }} />
                <Typography sx={{ minWidth: 0, fontWeight: 900, fontSize: compact ? 10.5 : 11.2, lineHeight: 1.25 }}>
                  {item.label}{item.detail ? <Box component="span" sx={{ color: "text.secondary", fontWeight: 750 }}> · {item.detail}</Box> : null}
                </Typography>
              </Box>
            ))}
            {items.length > (compact ? 6 : 12) ? <Typography sx={{ color: "text.secondary", fontSize: 10.2, fontWeight: 800 }}>+ {items.length - (compact ? 6 : 12)} elementos más</Typography> : null}
          </Stack>
        ) : null}
      </Box>
    );
  }

  if (state.mode !== "draft") return null;
  const preview = String(state.preview || "").trim();
  return (
    <Box
      sx={{
        mt: compact ? .55 : .8,
        px: compact ? .75 : 1,
        py: compact ? .6 : .8,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2.2,
        bgcolor: "action.hover",
      }}
    >
      <Stack direction="row" spacing={.55} alignItems="center" useFlexGap flexWrap="wrap">
        <Typography sx={{ fontWeight: 1000, fontSize: compact ? 10.8 : 11.5 }}>
          {state.title || "Borrador de mejora"}
        </Typography>
        <Chip size="small" label="No enviado" variant="outlined" sx={{ height: 22, fontWeight: 900, fontSize: 10 }} />
      </Stack>
      {preview ? (
        <Typography
          sx={{
            mt: .35,
            color: "text.secondary",
            fontWeight: 750,
            fontSize: compact ? 10.7 : 11.5,
            lineHeight: 1.35,
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: compact ? 2 : 3,
            overflow: "hidden",
          }}
        >
          {preview}
        </Typography>
      ) : null}
    </Box>
  );
}

export default function OwnerJarvis2({ currency = "ARS", onChanged, compact = false, dense = false, dailyGuide = false, viewingToday = true }) {
  const navigate = useNavigate();
  const voice = useOwnerVoiceOperator();
  const compactLayout = compact || dense;
  const [turn, setTurn] = React.useState(() => {
    const stored = readOwnerJarvisStoredTurn();
    const storedKind = String(stored?.session?.kind || "");
    // JARVIS_PATCH_NOTE [NO-PROACTIVE-IMPROVEMENT-QA-002]: no restaurar sesiones
    // legacy de QA de mejoras. Jarvis Lab sigue permitiendo revisión manual.
    if ((isRootNavigationTurn(stored) && Number(stored?.session?.version || 0) < 2)
      || storedKind === "improvement_review") {
      persistOwnerJarvisTurn(null);
      return null;
    }
    return stored;
  });
  const [text, setText] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [typing, setTyping] = React.useState(false);
  const [quickQueriesOpen, setQuickQueriesOpen] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState(() => {
    const saved = readCollapsedPreference();
    const mobileDefault = typeof window !== "undefined" && window.matchMedia?.("(max-width:599.95px)")?.matches;
    return saved == null ? (Boolean(compactLayout) || Boolean(mobileDefault)) : saved;
  });
  const turnRef = React.useRef(null);
  const voiceRef = React.useRef(voice);
  const onChangedRef = React.useRef(onChanged);
  turnRef.current = turn;
  voiceRef.current = voice;
  onChangedRef.current = onChanged;

  const applyResponse = React.useCallback(async (response, { speak = false, requestMessage = "", requestPath = "", inputSource = "text", conversationContext = null } = {}) => {
    const next = response?.turn || null;
    if (!next) return { handled: false };
    const journalPath = requestPath || `${window.location.pathname || ""}${window.location.search || ""}`;
    const assistantJournalText = [next.reply, next.question].filter(Boolean).join(" ").trim();
    if (assistantJournalText) appendOwnerJarvisConversationMessage("assistant", assistantJournalText, { path: journalPath });
    persistOwnerJarvisTurn(next);
    turnRef.current = next;
    setTurn(next);
    setError("");
    if (next.refreshOwner) {
      await onChangedRef.current?.(JARVIS_MUTATION_SCOPES);
      try { window.dispatchEvent(new CustomEvent("owner:jarvis-mutated", { detail: { scopes: JARVIS_MUTATION_SCOPES } })); } catch { /* noop */ }
    }
    const execution = executeOwnerJarvisActionPlan(navigate, next);
    const executionNotice = ownerJarvisActionExecutionNotice(execution);
    const message = [spokenTurn(next), executionNotice].filter(Boolean).join(" ");
    if (executionNotice) setError(executionNotice);
    const activeVoice = voiceRef.current;
    if (speak && message) {
      activeVoice?.guideToSection?.(next.highlightSection || "today-jarvis", message, {
        listenAfter: Boolean(activeVoice?.handsFree || activeVoice?.alwaysOnEnabled),
        force: true,
        scroll: next.highlightSection && next.highlightSection !== "today-jarvis",
      });
    }
    const failureReport = ownerJarvisActionExecutionFailureReport(next, execution, {
      utterance: requestMessage,
      currentPath: requestPath,
      inputSource,
      conversationContext,
    });
    if (failureReport) ownerApi.reportJarvisUnhandled(failureReport).catch(() => undefined);
    else {
      const successReport = ownerJarvisActionExecutionSuccessReport(next, execution, {
        utterance: requestMessage,
        currentPath: requestPath,
        inputSource,
      });
      if (successReport) ownerApi.reportJarvisResolution(successReport).catch(() => undefined);
    }
    return {
      handled: next.handled !== false,
      message,
      highlightSection: next.highlightSection || "today-jarvis",
    };
  }, [navigate]);

  const send = React.useCallback(async (message, { speak = false, inputSource = "text" } = {}) => {
    const clean = String(message || "").trim();
    const requestPath = `${window.location.pathname || ""}${window.location.search || ""}`;
    setLoading(true);
    setError("");
    try {
      appendOwnerJarvisConversationMessage("user", clean, { inputSource, path: requestPath });
      const conversationContext = ownerJarvisConversationContext();
      const response = await ownerApi.jarvisTurn({
        message: clean,
        session: turnRef.current?.session || null,
        currency,
        currentPath: requestPath,
        inputSource,
        conversationContext,
        runtimeContext: {
          authority: "backend",
          frontendInterpreter: false,
        },
      });
      return await applyResponse(response, { speak, requestMessage: clean, requestPath, inputSource, conversationContext });
    } catch (err) {
      setError(formatApiError(err, "Jarvis no pudo procesar esa respuesta."));
      return { handled: true, message: "No pude procesarlo ahora. Probá otra vez." };
    } finally {
      setLoading(false);
    }
  }, [applyResponse, currency]);

  const restart = React.useCallback(async ({ speak = null } = {}) => {
    persistOwnerJarvisTurn(null);
    turnRef.current = null;
    setTurn(null);
    setLoading(true);
    setError("");
    try {
      const response = await ownerApi.jarvisTurn({ message: "", session: null, currency });
      const shouldSpeak = speak == null ? Boolean(voiceRef.current?.handsFree || voiceRef.current?.alwaysOnEnabled) : Boolean(speak);
      await applyResponse(response, { speak: shouldSpeak });
    } catch (err) {
      setError(formatApiError(err, "No pude iniciar Jarvis."));
    } finally {
      setLoading(false);
    }
  }, [applyResponse, currency]);

  React.useEffect(() => {
    const onConversationReset = () => {
        persistOwnerJarvisTurn(null);
      turnRef.current = null;
      setTurn(null);
      setError("");
      setQuickQueriesOpen(false);
      if (/^\/dashboard\/owner\/?$/.test(window.location.pathname || "")) {
        window.setTimeout(() => restart({ speak: false }), 0);
      }
    };
    window.addEventListener("owner:jarvis-conversation-reset", onConversationReset);
    return () => window.removeEventListener("owner:jarvis-conversation-reset", onConversationReset);
  }, [restart]);

  React.useEffect(() => {
    if (turnRef.current) { setLoading(false); return; }
    if (compact) { setLoading(false); return; }
    restart();
  }, [compact, restart]);

  React.useEffect(() => {
    if (!voice?.registerConversationHandler) return undefined;
    return voice.registerConversationHandler(async ({ raw, inputSource }) => send(raw, { speak: false, inputSource: inputSource || "voice" }));
  }, [send, voice?.registerConversationHandler]);

  React.useEffect(() => {
    let active = true;
    let firstTimer = null;
    let pollTimer = null;
    const poll = async () => {
      if (!active || document.visibilityState !== "visible") return;
      const kind = String(turnRef.current?.session?.kind || "");
      if (kind && kind !== "idle") return;
      try {
        if (viewingToday) {
          const guardianResponse = await ownerApi.dayGuardian();
          const guardianTurn = guardianResponse?.turn || null;
          const currentKind = String(turnRef.current?.session?.kind || "");
          if (!active || (currentKind && currentKind !== "idle")) return;
          if (guardianTurn) {
            persistOwnerJarvisTurn(guardianTurn);
            turnRef.current = guardianTurn;
            setTurn(guardianTurn);
            const words = spokenTurn(guardianTurn);
            const activeVoice = voiceRef.current;
            if (words && activeVoice?.alwaysOnEnabled) activeVoice?.say?.(words, { listenAfter: true, force: true });
            return;
          }
        }
      } catch {
        // Guardian es proactivo: si su fuente falla, no interrumpimos al Owner.
      }
    };
    firstTimer = window.setTimeout(poll, 2500);
    pollTimer = window.setInterval(poll, 4 * 60 * 1000);
    return () => {
      active = false;
      window.clearTimeout(firstTimer);
      window.clearInterval(pollTimer);
    };
  }, [viewingToday]);

  const submitText = async () => {
    const clean = text.trim();
    if (!clean || loading) return;
    setText("");
    await send(clean, { speak: Boolean(voice?.handsFree) });
  };

  const hardRestart = async () => {
    if (loading) return;
    const onHome = /^\/dashboard\/owner\/?$/.test(window.location.pathname || "");
    if (voice?.resetOwnerConversation) {
      await voice.resetOwnerConversation({ navigateHome: false, announce: false });
      if (!onHome) await restart({ speak: false });
      return;
    }
    await restart({ speak: false });
  };

  const goHomeAndReset = async () => {
    if (loading) return;
    if (voice?.resetOwnerConversation) {
      await voice.resetOwnerConversation({ navigateHome: true, announce: true });
      return;
    }
    await send("inicio", { speak: false });
  };

  const choose = async (choice) => {
    if (loading) return;
    await send(choice.value, { speak: Boolean(voice?.handsFree) });
  };

  const talk = () => {
    const words = spokenTurn(turn) || "Decime qué querés resolver.";
    if (voice?.say) voice.say(words, { listenAfter: true, force: true });
    else voice?.startGlobalConversation?.();
  };

  const togglePower = () => {
    if (voice?.alwaysOnEnabled) voice?.disableAlwaysOn?.();
    else voice?.enableAlwaysOn?.({ greet: true });
  };

  const toggleCollapsed = () => {
    setCollapsed((value) => {
      const next = !value;
      persistCollapsedPreference(next);
      if (next) {
        setTyping(false);
        setQuickQueriesOpen(false);
      }
      return next;
    });
  };

  const applyQuickQueryTemplate = (template) => {
    setText(String(template || ""));
    setTyping(true);
    setQuickQueriesOpen(false);
  };

  const quickQueryExamples = [
    { label: "Gastos por categoría", template: "¿Cuánto gasté en [categoría] este mes?" },
    { label: "Ingresos por categoría", template: "Mostrame los ingresos de la categoría [categoría] de este mes" },
    { label: "Saldo de cuenta", template: "¿Cuánto tengo en [cuenta]?" },
  ];

  const sessionKind = String(turn?.session?.kind || "");
  const navigationPath = Array.isArray(turn?.navigation?.path) && turn.navigation.path.length
    ? turn.navigation.path
    : sessionKind === "project_review"
      ? [
          "Inicio",
          "Proyectos",
          turn?.session?.projectName || "Proyecto",
          ...(String(turn?.session?.stage || "").startsWith("monthly_") ? ["Mensualidad"] : []),
          ...(String(turn?.session?.stage || "").startsWith("contract_") ? ["Contrato / cuotas"] : []),
        ]
      : sessionKind === "task_planning"
        ? ["Inicio", "Proyectos", turn?.session?.projectName || "Proyecto", "Tarea"]
        : sessionKind === "daily_cycle"
          ? ["Inicio", "Mi día"]
          : sessionKind === "day_guardian"
            ? ["Inicio", "Mi día", "Repaso"]
          : sessionKind === "improvement_review"
            ? ["Inicio", "Jarvis Lab"]
            : sessionKind === "product_feedback_capture"
              ? ["Inicio", "Jarvis", "Borrador de mejora"]
              : sessionKind === "operational_draft"
                ? ["Inicio", "Jarvis", "Carga operativa"]
                : ["Inicio"];
  const contextualKeywords = Array.isArray(turn?.navigation?.keywords) && turn.navigation.keywords.length
    ? turn.navigation.keywords
    : sessionKind === "project_review" && String(turn?.session?.stage || "").startsWith("monthly_")
      ? ["cambiar monto", "día de cobro", "billing", "próximos cobros", "volver", "inicio"]
      : sessionKind === "project_review" && String(turn?.session?.stage || "").startsWith("contract_")
        ? ["editar contrato", "cuotas semanales", "cuotas mensuales", "próximos cobros", "volver", "inicio"]
        : ["project_review", "task_planning"].includes(sessionKind)
          ? ["estado", "pendientes", "cobros", "mensualidad", "contrato", "volver", "inicio"]
          : sessionKind === "daily_cycle"
        ? ["hecha", "más tarde", "agenda", "volver", "inicio"]
        : sessionKind === "improvement_review"
          ? ["quedó bien", "sigue mal", "después", "inicio"]
          : sessionKind === "product_feedback_capture"
            ? ["agregar más", "guardar en backlog", "descartar"]
            : sessionKind === "operational_draft"
              ? ["guardar todo", "agregar más", "editar cliente", "editar proyecto", "descartar"]
              : [];
  const canGoBack = navigationPath.length > 1 || Boolean(turn?.session);
  const currentLocationLabel = navigationPath.slice(-2).join(" › ") || "Inicio";

  if (dailyGuide) {
    return (
      <Box
        data-owner-jarvis-section="today-jarvis"
        sx={{
          ...surfaceSx,
          minWidth: 0,
          px: { xs: .9, sm: 1.15 },
          py: { xs: .75, sm: .9 },
          overflow: "hidden",
          background: "linear-gradient(145deg, rgba(243,90,58,.075), rgba(255,255,255,0) 58%)",
        }}
      >
        <Stack direction="row" spacing={.7} alignItems="flex-start" sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              bgcolor: "primary.main",
              color: "primary.contrastText",
              display: "grid",
              placeItems: "center",
              flex: "0 0 auto",
            }}
          >
            {loading ? <CircularProgress size={15} color="inherit" /> : <AutoAwesomeRoundedIcon sx={{ fontSize: 17 }} />}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={.55} alignItems="baseline" useFlexGap flexWrap="wrap">
              <Typography sx={{ fontWeight: 1000, fontSize: 10.5, color: "primary.main", letterSpacing: ".08em", textTransform: "uppercase" }}>Jarvis</Typography>
              <Typography sx={{ color: "text.secondary", fontWeight: 800, fontSize: 10.5 }}>Guía del día</Typography>
            </Stack>
            {turn?.reply ? (
              <Typography
                sx={{
                  mt: .15,
                  color: "text.secondary",
                  fontWeight: 750,
                  fontSize: { xs: 11.5, sm: 12.5 },
                  lineHeight: 1.3,
                  display: { xs: "-webkit-box", sm: "block" },
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: { xs: 2, sm: "unset" },
                  overflow: "hidden",
                }}
              >
                {turn.reply}
              </Typography>
            ) : null}
            <Typography
              sx={{
                mt: turn?.reply ? .2 : .1,
                fontWeight: 1000,
                fontSize: { xs: 14.5, sm: 16.5 },
                letterSpacing: "-.025em",
                lineHeight: 1.2,
                display: { xs: "-webkit-box", sm: "block" },
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: { xs: 3, sm: "unset" },
                overflow: "hidden",
              }}
            >
              {turn?.question || (loading ? "Estoy mirando qué necesita tu atención…" : "¿Qué querés resolver?")}
            </Typography>
          </Box>
        </Stack>

        {error ? <Alert severity="error" sx={{ mt: .65, py: 0, "& .MuiAlert-message": { py: .35, fontSize: 11.5 } }}>{error}</Alert> : null}
        <OwnerJarvisUiState turn={turn} compact />

        {turn?.choices?.length ? (
          <Box
            sx={{
              display: "flex",
              gap: .55,
              mt: .7,
              overflowX: "auto",
              overscrollBehaviorX: "contain",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {turn.choices.slice(0, 8).map((choice) => (
              <Button
                key={`${choice.value}-${choice.label}`}
                onClick={() => choose(choice)}
                disabled={loading}
                variant="outlined"
                sx={{
                  minHeight: 34,
                  minWidth: { xs: 116, sm: 0 },
                  flex: { xs: "0 0 auto", sm: "0 1 auto" },
                  px: .85,
                  borderRadius: 2,
                  justifyContent: "flex-start",
                  textTransform: "none",
                  fontWeight: 950,
                  fontSize: 10.8,
                  lineHeight: 1.15,
                  textAlign: "left",
                }}
              >
                {choice.label}
              </Button>
            ))}
          </Box>
        ) : null}

        <Stack direction="row" spacing={.5} alignItems="center" sx={{ mt: .7, minWidth: 0 }}>
          <TextField
            fullWidth
            size="small"
            value={text}
            disabled={loading}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); submitText(); } }}
            placeholder="Escribile a Jarvis…"
            inputProps={{ "aria-label": "Escribile a Jarvis" }}
            sx={{
              minWidth: 0,
              "& .MuiInputBase-root": { minHeight: 38, borderRadius: 2.15, fontSize: 12.5, fontWeight: 750 },
              "& .MuiInputBase-input": { py: .8 },
            }}
          />
          <Tooltip title="Enviar">
            <span>
              <IconButton
                onClick={submitText}
                disabled={!text.trim() || loading}
                color="primary"
                aria-label="Enviar a Jarvis"
                sx={{ width: 38, height: 38, flex: "0 0 auto" }}
              >
                <SendRoundedIcon sx={{ fontSize: 19 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>
    );
  }

  if (collapsed) {
    return (
      <Box data-owner-jarvis-section="today-jarvis" sx={{ ...surfaceSx, px: 1.05, py: .7, background: "linear-gradient(145deg, rgba(243,90,58,.075), rgba(255,255,255,0) 60%)" }}>
        <Stack direction="row" spacing={.75} alignItems="center">
          <Box sx={{ width: 30, height: 30, borderRadius: "50%", bgcolor: "primary.main", color: "primary.contrastText", display: "grid", placeItems: "center", flex: "0 0 auto" }}>
            {loading ? <CircularProgress size={15} color="inherit" /> : <AutoAwesomeRoundedIcon sx={{ fontSize: 17 }} />}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={.55} alignItems="baseline" flexWrap="wrap" useFlexGap>
              <Typography sx={{ fontWeight: 1000, fontSize: 11.5, color: "primary.main", letterSpacing: ".06em", textTransform: "uppercase" }}>Jarvis</Typography>
              <Typography sx={{ color: "text.secondary", fontWeight: 850, fontSize: 11.2 }}>{currentLocationLabel}</Typography>
            </Stack>
            <Typography noWrap sx={{ mt: .05, color: "text.secondary", fontSize: 10.7 }}>{turn?.question || turn?.reply || "La conversación sigue activa aunque ocultes el panel."}</Typography>
          </Box>
          {turn?.uiState?.mode === "draft"
            ? <Chip size="small" label="Borrador · no enviado" variant="outlined" sx={{ display: { xs: "none", sm: "inline-flex" }, maxWidth: 160, fontWeight: 900 }} />
            : turn?.uiState?.mode === "operational_draft"
              ? <Chip size="small" label="Carga · sin guardar" variant="outlined" sx={{ display: { xs: "none", sm: "inline-flex" }, maxWidth: 160, fontWeight: 900 }} />
            : turn?.signal?.label
              ? <Chip size="small" label={turn.signal.label} variant="outlined" sx={{ display: { xs: "none", sm: "inline-flex" }, maxWidth: 130, fontWeight: 850 }} />
              : null}
          <Tooltip title="Mostrar Jarvis"><IconButton onClick={toggleCollapsed} sx={{ width: 34, height: 34 }}><ExpandMoreRoundedIcon /></IconButton></Tooltip>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      data-owner-jarvis-section="today-jarvis"
      sx={{
        ...surfaceSx,
        minHeight: compactLayout ? "auto" : { xs: 330, sm: 360, md: 390 },
        p: compactLayout ? { xs: 1, sm: 1.15 } : { xs: 1.4, sm: 1.8, md: 2.2 },
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background: "linear-gradient(145deg, rgba(243,90,58,.095), rgba(255,255,255,0) 48%)",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Box sx={{ width: 48, height: 48, borderRadius: "50%", bgcolor: "primary.main", color: "primary.contrastText", display: "grid", placeItems: "center", flex: "0 0 auto", boxShadow: "0 10px 28px rgba(243,90,58,.22)" }}>
          {loading ? <CircularProgress size={22} color="inherit" /> : <AutoAwesomeRoundedIcon />}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontWeight: 1000, fontSize: 12, color: "primary.main", letterSpacing: ".08em", textTransform: "uppercase" }}>Jarvis</Typography>
          {!compact ? <Typography sx={{ color: "text.secondary", fontWeight: 750, fontSize: 12.5 }}>Te saco contexto · ordeno el proyecto · te digo qué sigue</Typography> : <Typography sx={{ color: "text.secondary", fontWeight: 750, fontSize: 11.5 }}>Sigo con la misma conversación mientras mirás la ficha.</Typography>}
        </Box>
        {turn?.signal?.label ? <Chip size="small" label={turn.signal.label} variant="outlined" sx={{ maxWidth: 160, fontWeight: 850 }} /> : null}
        <Tooltip title="Ocultar Jarvis"><IconButton onClick={toggleCollapsed} sx={{ width: 42, height: 42 }}><ExpandLessRoundedIcon /></IconButton></Tooltip>
        <Tooltip title={voice?.alwaysOnEnabled ? "Apagar Jarvis" : "Encender Jarvis"}>
          <span>
            <IconButton
              onClick={togglePower}
              disabled={!voice?.speechSupported}
              color={voice?.alwaysOnEnabled ? "primary" : "default"}
              sx={{ width: 42, height: 42 }}
            >
              <PowerSettingsNewRoundedIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Reiniciar conversación"><span><IconButton onClick={hardRestart} disabled={loading} sx={{ width: 42, height: 42 }}><ReplayRoundedIcon /></IconButton></span></Tooltip>
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={.55} alignItems={{ sm: "center" }} sx={{ mt: .8 }}>
        <Stack direction="row" spacing={.4} alignItems="center" flexWrap="wrap" useFlexGap sx={{ minWidth: 0, flex: 1 }}>
          {navigationPath.map((item, index) => (
            <React.Fragment key={`${item}-${index}`}>
              {index ? <Typography sx={{ color: "text.disabled", fontSize: 11, fontWeight: 900 }}>›</Typography> : null}
              <Typography sx={{ color: index === navigationPath.length - 1 ? "text.primary" : "text.secondary", fontSize: 11.5, fontWeight: index === navigationPath.length - 1 ? 1000 : 800 }}>{item}</Typography>
            </React.Fragment>
          ))}
        </Stack>
        <Stack direction="row" spacing={.35}>
          {canGoBack ? <Button size="small" onClick={() => send("volver", { speak: false })} disabled={loading} sx={{ minWidth: 0, px: .75, textTransform: "none", fontWeight: 900, fontSize: 11 }}>Volver</Button> : null}
          {navigationPath.length > 1 ? <Button size="small" onClick={goHomeAndReset} disabled={loading} sx={{ minWidth: 0, px: .75, textTransform: "none", fontWeight: 900, fontSize: 11 }}>Inicio</Button> : null}
        </Stack>
      </Stack>

      {contextualKeywords.length ? (
        <Typography sx={{ mt: .35, color: "text.secondary", fontSize: 10.8, lineHeight: 1.35 }}>
          Decí: {contextualKeywords.slice(0, 8).map((word) => `“${word}”`).join(" · ")}
        </Typography>
      ) : null}
      <OwnerJarvisUiState turn={turn} compact={compactLayout} />
      {isRootNavigationTurn(turn) ? (
        <Box sx={{ mt: .45 }}>
          <Button
            size="small"
            variant={quickQueriesOpen ? "contained" : "outlined"}
            onClick={() => setQuickQueriesOpen((value) => !value)}
            disabled={loading}
            sx={{ minHeight: 30, px: .9, borderRadius: 2, textTransform: "none", fontWeight: 950, fontSize: 11.2 }}
          >
            Consultas rápidas
          </Button>
          {quickQueriesOpen ? (
            <Box sx={{ mt: .55, p: .75, border: "1px solid", borderColor: "divider", borderRadius: 2.2, bgcolor: "action.hover" }}>
              <Typography sx={{ color: "text.secondary", fontSize: 10.7, lineHeight: 1.35, mb: .55 }}>
                Tocá un ejemplo para llevarlo al teclado. Reemplazá [categoría] o [cuenta] y enviáselo a Jarvis. Estas consultas viven sólo en Inicio.
              </Typography>
              <Stack direction="row" spacing={.45} flexWrap="wrap" useFlexGap>
                {quickQueryExamples.map((item) => (
                  <Button
                    key={item.label}
                    size="small"
                    variant="text"
                    onClick={() => applyQuickQueryTemplate(item.template)}
                    sx={{ minHeight: 30, px: .75, justifyContent: "flex-start", textTransform: "none", fontWeight: 900, fontSize: 10.9 }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Stack>
              <Typography sx={{ mt: .45, color: "text.secondary", fontSize: 10.4 }}>
                Ej.: “Mostrame los ingresos de la categoría ventas” → Jarvis calcula el total y abre ese lote filtrado para revisarlo o cambiar sus vínculos.
              </Typography>
            </Box>
          ) : null}
        </Box>
      ) : null}

      {error ? <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert> : null}

      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", py: compactLayout ? { xs: .8, sm: 1 } : { xs: 1.6, sm: 2.2 } }}>
        {turn?.reply ? (
          <Typography sx={{ color: "text.secondary", fontWeight: 800, fontSize: { xs: 13, sm: 14.5 }, lineHeight: 1.4, mb: .45 }}>
            {turn.reply}
          </Typography>
        ) : null}
        {explainOwnerJarvisReasons(turn?.reasons) ? (
          <Typography sx={{ color: "text.secondary", fontWeight: 750, fontSize: { xs: 12, sm: 13 }, lineHeight: 1.35, mb: .55 }}>
            {explainOwnerJarvisReasons(turn.reasons)}
          </Typography>
        ) : null}
        <Typography sx={{ fontWeight: 1000, fontSize: compactLayout ? { xs: 18, sm: 20 } : { xs: 24, sm: 30, md: 34 }, letterSpacing: "-.04em", lineHeight: 1.08, maxWidth: 820 }}>
          {turn?.question || (loading ? "Estoy mirando qué necesita tu atención…" : "¿Qué querés resolver?")}
        </Typography>

        {turn?.choices?.length ? (
          <Box sx={{ display: "grid", gridTemplateColumns: compactLayout ? { xs: "repeat(2,minmax(0,1fr))", sm: "repeat(3,minmax(0,1fr))" } : { xs: "1fr 1fr", sm: "repeat(2,minmax(0,1fr))", md: "repeat(3,minmax(0,1fr))" }, gap: .75, mt: compactLayout ? .9 : 1.5, maxWidth: compactLayout ? "none" : 760 }}>
            {turn.choices.slice(0, 8).map((choice) => (
              <Button
                key={`${choice.value}-${choice.label}`}
                onClick={() => choose(choice)}
                disabled={loading}
                variant="outlined"
                sx={{ minHeight: compactLayout ? 40 : { xs: 52, sm: 58 }, borderRadius: 2.6, justifyContent: "flex-start", px: compactLayout ? 1 : 1.35, textTransform: "none", fontWeight: 1000, fontSize: compactLayout ? 11.5 : { xs: 12.5, sm: 14.5 }, textAlign: "left" }}
              >
                {choice.label}
              </Button>
            ))}
          </Box>
        ) : null}
      </Box>

      <Stack spacing={.7}>
        <Stack direction="row" spacing={.65}>
          <Button
            fullWidth
            onClick={talk}
            disabled={!voice?.speechSupported || loading}
            variant="contained"
            startIcon={<MicRoundedIcon />}
            sx={{ minHeight: compactLayout ? 42 : 54, borderRadius: 2.8, textTransform: "none", fontWeight: 1000, fontSize: compactLayout ? 13 : 15 }}
          >
            {voice?.isListening ? "Te escucho…" : (voice?.alwaysOnEnabled ? "Hablar con Jarvis" : "Responder hablando")}
          </Button>
          <Tooltip title={typing ? "Ocultar teclado" : "Escribir"}>
            <span>
              <IconButton onClick={() => setTyping((value) => !value)} disabled={loading} sx={{ width: compactLayout ? 42 : 54, height: compactLayout ? 42 : 54, border: "1px solid", borderColor: "divider", borderRadius: 2.8 }}>
                <KeyboardRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        {typing ? (
          <Stack direction="row" spacing={.65} alignItems="center">
            <TextField
              autoFocus
              fullWidth
              size="small"
              value={text}
              disabled={loading}
              onChange={(event) => setText(event.target.value)}
              onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); submitText(); } }}
              placeholder="Escribile a Jarvis…"
              sx={{ "& .MuiInputBase-root": { minHeight: 48, borderRadius: 2.6, fontSize: 14, fontWeight: 750 } }}
            />
            <IconButton onClick={submitText} disabled={!text.trim() || loading} color="primary" sx={{ width: 48, height: 48 }}><SendRoundedIcon /></IconButton>
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
}
